import {
	Box,
	Flex,
	HStack,
	Icon,
	Radio,
	RadioGroup,
	Stack,
	Text,
	useColorModeValue,
} from '@chakra-ui/react';
import { MdCheck } from 'react-icons/md';
import { useAppSettings, AnimationLevel } from '../../contexts/AppSettingsContext';
import { themePresets, presetIds, PresetId } from '../../theme/presets';
import { aestheticIds, AestheticId } from '../../theme/aesthetics';
import { typography } from '../../theme/tokens';
import { translate, TranslationKeys } from '../../i18n';

export default function AppearancePanel() {
	const {
		themePreset,
		setThemePreset,
		aesthetic,
		setAesthetic,
		animationLevel,
		setAnimationLevel,
	} = useAppSettings();

	const textColor = useColorModeValue('secondaryGray.900', 'white');
	const mutedColor = useColorModeValue('secondaryGray.600', 'gray.400');
	const borderColor = useColorModeValue('#E5E7EB', 'whiteAlpha.100');
	const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.50');
	const swatchBorder = useColorModeValue('blackAlpha.100', 'whiteAlpha.200');

	return (
		<Box p="16px" minW="260px">
			{/* ---- Aesthetic Direction ---- */}
			<Text {...typography.formSectionLabel} color={mutedColor} mb="12px">
				{translate('appearance.style')}
			</Text>
			<Stack spacing="6px" mb="20px">
				{aestheticIds.map((id) => {
					const isActive = id === aesthetic;
					const nameKey = `appearance.aesthetic_${id}` as TranslationKeys;
					const descKey = `appearance.aesthetic_${id}_desc` as TranslationKeys;
					return (
						<Flex
							key={id}
							align="center"
							justify="space-between"
							px="12px"
							py="10px"
							borderRadius="10px"
							cursor="pointer"
							border="1.5px solid"
							borderColor={isActive ? 'brand.500' : 'transparent'}
							bg={isActive ? hoverBg : 'transparent'}
							_hover={{ bg: hoverBg }}
							transition="all 0.15s ease"
							onClick={() => setAesthetic(id as AestheticId)}
						>
							<HStack spacing="10px">
								{/* Aesthetic visual indicator */}
								<HStack spacing="4px">
									{id === 'classic' ? (
										<>
											<Box w="16px" h="16px" borderRadius="full" bg="#F4F5F7" border="1px solid" borderColor={swatchBorder} />
											<Box w="16px" h="16px" borderRadius="full" bg="#E5E7EB" border="1px solid" borderColor={swatchBorder} />
											<Box w="16px" h="16px" borderRadius="full" bg="#1A1D26" border="1px solid" borderColor={swatchBorder} />
										</>
									) : (
										<>
											<Box w="16px" h="16px" borderRadius="full" bg="#F7F6F3" border="1px solid" borderColor={swatchBorder} />
											<Box w="16px" h="16px" borderRadius="full" bg="#E5E2DC" border="1px solid" borderColor={swatchBorder} />
											<Box w="16px" h="16px" borderRadius="full" bg="#2C2A26" border="1px solid" borderColor={swatchBorder} />
										</>
									)}
								</HStack>
								<Box>
									<Text fontSize="sm" fontWeight="600" color={textColor} lineHeight="1.2">
										{translate(nameKey)}
									</Text>
									<Text fontSize="xs" color={mutedColor} lineHeight="1.3">
										{translate(descKey)}
									</Text>
								</Box>
							</HStack>
							{isActive && (
								<Icon as={MdCheck} color="brand.500" boxSize="16px" />
							)}
						</Flex>
					);
				})}
			</Stack>

			{/* ---- Divider ---- */}
			<Box h="1px" bg={borderColor} mb="16px" />

			{/* ---- Theme Selection ---- */}
			<Text {...typography.formSectionLabel} color={mutedColor} mb="12px">
				{translate('appearance.theme')}
			</Text>
			<Stack spacing="6px" mb="20px">
				{presetIds.map((id) => {
					const preset = themePresets[id];
					const isActive = id === themePreset;
					const nameKey = `appearance.preset_${preset.nameKey}` as TranslationKeys;
					const descKey = `appearance.preset_${preset.nameKey}_desc` as TranslationKeys;
					return (
						<Flex
							key={id}
							align="center"
							justify="space-between"
							px="12px"
							py="10px"
							borderRadius="10px"
							cursor="pointer"
							border="1.5px solid"
							borderColor={isActive ? preset.swatches[0] : 'transparent'}
							bg={isActive ? hoverBg : 'transparent'}
							_hover={{ bg: hoverBg }}
							transition="all 0.15s ease"
							onClick={() => setThemePreset(id as PresetId)}
						>
							<HStack spacing="10px">
								{/* Swatch circles */}
								<HStack spacing="4px">
									{preset.swatches.map((color, i) => (
										<Box
											key={i}
											w="16px"
											h="16px"
											borderRadius="full"
											bg={color}
											border="1px solid"
											borderColor={swatchBorder}
										/>
									))}
								</HStack>
								<Box>
									<Text fontSize="sm" fontWeight="600" color={textColor} lineHeight="1.2">
										{translate(nameKey)}
									</Text>
									<Text fontSize="xs" color={mutedColor} lineHeight="1.3">
										{translate(descKey)}
									</Text>
								</Box>
							</HStack>
							{isActive && (
								<Icon as={MdCheck} color={preset.swatches[0]} boxSize="16px" />
							)}
						</Flex>
					);
				})}
			</Stack>

			{/* ---- Divider ---- */}
			<Box h="1px" bg={borderColor} mb="16px" />

			{/* ---- Animation Level ---- */}
			<Text {...typography.formSectionLabel} color={mutedColor} mb="12px">
				{translate('appearance.animations')}
			</Text>
			<RadioGroup
				value={animationLevel}
				onChange={(val) => setAnimationLevel(val as AnimationLevel)}
			>
				<Stack spacing="8px">
					{animationOptions.map((opt) => (
						<Radio
							key={opt.value}
							value={opt.value}
							colorScheme="brand"
							size="sm"
						>
							<Box ml="2px">
								<Text fontSize="sm" fontWeight="500" color={textColor} lineHeight="1.2">
									{translate(opt.labelKey)}
								</Text>
								<Text fontSize="xs" color={mutedColor} lineHeight="1.4">
									{translate(opt.descKey)}
								</Text>
							</Box>
						</Radio>
					))}
				</Stack>
			</RadioGroup>
		</Box>
	);
}

const animationOptions: { value: AnimationLevel; labelKey: TranslationKeys; descKey: TranslationKeys }[] = [
	{ value: 'full', labelKey: 'appearance.animation_full', descKey: 'appearance.animation_full_desc' },
	{ value: 'reduced', labelKey: 'appearance.animation_reduced', descKey: 'appearance.animation_reduced_desc' },
	{ value: 'none', labelKey: 'appearance.animation_none', descKey: 'appearance.animation_none_desc' },
];
